import { useEffect } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useTranslation } from 'react-i18next'

import { Button, Box as DarkBox, TrashIcon } from '@hivewatch/dark'

import { DeviceFeed } from '@/features/device-feed'
import {
  MSEVideoPlayer,
  VideoFeedEvent,
  VideoPlayer,
  useFindDeviceFeed,
} from '@/features/video-player'
import { useDevicePopoverDetailsQuery } from '@/graphql/generated/hooks'
import { DeviceType } from '@/graphql/generated/schemas'
import { AnalyticsScreen, useMixpanel } from '@/utils/analytics'

import { DarkFloorPlanDeviceMarker } from './DarkFloorPlanDeviceMarker'
import { darkPopoverStyles } from './DarkFloorPlanPopover.styles'

interface DarkDevicePopoverContentIProps {
  colorScheme: 'dark' | 'light'
  deviceId: string
  deviceName: string
  deviceType: DeviceType
  showRemoveAction?: boolean
  canRemove?: boolean
  isRemoving?: boolean
  onRemove?: () => void
}

export const DarkDevicePopoverContent = ({
  colorScheme,
  deviceId,
  deviceName,
  deviceType,
  showRemoveAction = false,
  canRemove = false,
  isRemoving = false,
  onRemove,
}: DarkDevicePopoverContentIProps) => {
  const { t } = useTranslation()
  const isCameraDevice = deviceType === DeviceType.Camera
  const s = darkPopoverStyles(colorScheme)

  const isMSEStreamingEnabled = useFeatureFlag(
    FeatureFlag.authenticatedProxyStreaming
  )

  const { data, loading: isLoading } = useDevicePopoverDetailsQuery({
    nextFetchPolicy: 'cache-first',
    variables: { id: deviceId },
  })

  const { createCameraEventHandler, markStreamRequested } = useMixpanel(
    AnalyticsScreen.DeviceHoverModal
  )

  useEffect(() => {
    markStreamRequested()
  }, [])

  const mixpanelData = () => ({
    device_id: deviceId,
    facility_id: data?.device?.facility?.id,
    floor_id: data?.device?.floor?.id,
    floor_name: data?.device?.floor?.name,
    streaming_type: data?.device?.camera?.streamingType,
  })

  const handleCameraEvent = createCameraEventHandler(mixpanelData())

  const {
    stream: { url: cameraFeedUrl, isLoading: isFeedDataLoading, errorMessage },
    nextCamera,
    previousCamera,
    availableCamerasIds,
    currentCameraId,
  } = useFindDeviceFeed(deviceId, handleCameraEvent)

  return (
    <DarkBox
      data-testid='DarkDevicePopoverContent:wrapper'
      display='flex'
      flexDirection='column'
    >
      {isCameraDevice && (
        <DarkBox {...s.feedFrame}>
          <DeviceFeed
            isLoading={isFeedDataLoading || isLoading}
            onClickNext={nextCamera}
            onClickPrevious={previousCamera}
            showCameraSwitcher={availableCamerasIds.length > 1}
          >
            {isMSEStreamingEnabled && currentCameraId ? (
              <MSEVideoPlayer
                autoplay={true}
                cameraId={currentCameraId}
                errorLabel={errorMessage}
                onEvent={handleCameraEvent}
                onStartPlaying={() => handleCameraEvent(VideoFeedEvent.Playing)}
                placeholderHeight='204px'
                showControls={true}
              />
            ) : (
              <VideoPlayer
                autoplay={true}
                errorLabel={errorMessage}
                liveEnabled={true}
                liveOnly
                onStartPlaying={() => handleCameraEvent(VideoFeedEvent.Playing)}
                pipActive={false}
                placeholderHeight='204px'
                url={cameraFeedUrl}
              />
            )}
          </DeviceFeed>
        </DarkBox>
      )}
      <DarkBox {...s.deviceRow}>
        <DarkFloorPlanDeviceMarker
          colorScheme={colorScheme}
          deviceType={deviceType}
        />
        <DarkBox {...s.deviceName}>{deviceName}</DarkBox>
        {showRemoveAction && (
          <DarkBox flexShrink={0} ml='auto'>
            <Button
              aria-label={t('floors:plan_remove_device_action')}
              colorScheme={colorScheme}
              disabled={!canRemove}
              isLoading={isRemoving}
              leftIcon={<TrashIcon color='currentColor' size={16} />}
              onClick={onRemove}
              size='sm'
              variant='danger'
            >
              {isRemoving
                ? t('floors:plan_remove_device_loading')
                : t('floors:plan_remove_device_action')}
            </Button>
          </DarkBox>
        )}
      </DarkBox>
    </DarkBox>
  )
}
